import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, FlameIcon } from 'lucide-react';
const STORAGE_KEY = 'dmt_offer_deadline';
const DURATION_MS = 12 * 60 * 60 * 1000; // 12 hours
const getDeadline = (): number => {
  if (typeof window === 'undefined') return Date.now() + DURATION_MS;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const ts = parseInt(stored, 10);
    if (!isNaN(ts) && ts > Date.now()) return ts;
  }
  const newDeadline = Date.now() + DURATION_MS;
  localStorage.setItem(STORAGE_KEY, String(newDeadline));
  return newDeadline;
};
const formatUnit = (n: number) => String(Math.max(0, n)).padStart(2, '0');
interface TimeBlockProps {
  value: string;
  label: string;
}
const TimeBlock: React.FC<TimeBlockProps> = ({
  value,
  label
}) => <div className="flex flex-col items-center" data-id="element-147">
    <div className="bg-cosmic-900/80 border border-amber-400/40 rounded-md px-2 py-1 md:px-3 md:py-1.5 min-w-[42px] md:min-w-[52px] text-center shadow-[inset_0_0_10px_rgba(245,197,66,0.1)]" data-id="element-148">
      <span className="font-serif font-bold text-lg md:text-2xl text-amber-300 tabular-nums tracking-wider" data-id="element-149">
        {value}
      </span>
    </div>
    <span className="text-[9px] md:text-[10px] uppercase tracking-widest text-amber-200/60 mt-1 font-sans" data-id="element-150">
      {label}
    </span>
  </div>;
export const CountdownBanner = () => {
  const [deadline] = useState<number>(() => getDeadline());
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const remaining = Math.max(0, deadline - now);
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  return <motion.div initial={{
    y: -40,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} className="sticky top-0 z-40 w-full bg-gradient-to-r from-cosmic-900 via-cosmic-800 to-cosmic-900 border-b border-amber-500/30 shadow-[0_4px_20px_rgba(0,0,0,0.5)]" data-id="element-151">
      {/* Subtle pulsing glow */}
      <motion.div animate={{
      opacity: [0.3, 0.6, 0.3]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/10 to-transparent pointer-events-none" data-id="element-152" />

      <div className="relative max-w-5xl mx-auto px-3 py-2.5 md:py-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-5" data-id="element-153">
        {/* Label */}
        <div className="flex items-center gap-2 text-amber-200" data-id="element-154">
          <FlameIcon className="w-4 h-4 md:w-5 md:h-5 text-amber-400 flex-shrink-0" data-id="element-155" />
          <p className="font-serif text-xs md:text-sm tracking-wide" data-id="element-156">
            <span className="font-semibold text-amber-300" data-id="element-157">
              75% de descuento
            </span>
            <span className="hidden sm:inline text-amber-200/70" data-id="element-158">
              {' '}
              · termina en
            </span>
          </p>
        </div>

        {/* Timer */}
        <div className="flex items-center gap-1.5 md:gap-2" data-id="element-159">
          <ClockIcon className="w-4 h-4 text-amber-400/70 mr-1 hidden md:block" data-id="element-160" />
          <TimeBlock value={formatUnit(hours)} label="Horas" data-id="element-161" />
          <span className="text-amber-400 font-bold text-lg md:text-xl pb-3 md:pb-4" data-id="element-162">
            :
          </span>
          <TimeBlock value={formatUnit(minutes)} label="Min" data-id="element-163" />
          <span className="text-amber-400 font-bold text-lg md:text-xl pb-3 md:pb-4" data-id="element-164">
            :
          </span>
          <TimeBlock value={formatUnit(seconds)} label="Seg" data-id="element-165" />
        </div>
      </div>
    </motion.div>;
};